import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getPrivyEmbeddedConfig } from "./privyEmbeddedConfig.js";
import { formatMarketTokenAmount, marketPaymentTokenForDisplay } from "./marketTokenDisplay.js";
import { recipientFundingSol } from "./voteTransactions.js";
import { formatWalletAddress } from "./walletAddress.js";
import {
  SIGNING_MODE_EMBEDDED_PRESIGN_SUBMIT,
  SIGNING_MODE_EMBEDDED_SIGN_ONLY,
  getWalletSigningMode
} from "./walletSigningReadiness.js";

const fallbackFeeText = "Network fee applies";

export function isPrivyEmbeddedVoteWallet(wallet) {
  const signingMode = getWalletSigningMode(wallet);
  return Boolean(
    wallet?.connector?.provider === "privy" &&
      (
        (signingMode === SIGNING_MODE_EMBEDDED_PRESIGN_SUBMIT && typeof wallet?.signAndSendTransaction === "function") ||
        (signingMode === SIGNING_MODE_EMBEDDED_SIGN_ONLY && typeof wallet?.signTransaction === "function")
      )
  );
}

export function normalizePrivyVoteNetworkLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "mainnet-beta" || normalized === "mainnet") return "mainnet";
  if (normalized === "testnet") return "testnet";
  if (normalized === "devnet") return "devnet";
  return "mainnet";
}

export function buildPrivyVoteConfirmationDetails({
  market,
  optionId,
  voteAmount,
  intent,
  networkId,
  expectedUserWallet,
  estimatedFeeText = ""
}) {
  const selectedOption = Array.isArray(market?.options)
    ? market.options.find((option) => option?.id === optionId)
    : null;
  const walletAddress = String(expectedUserWallet || "").trim();
  const tokenContext = intent?.paymentToken || market;
  const tokenRows = buildTokenVoteRows({ intent, tokenContext, voteAmount });
  const accountFundingText = recipientFundingSol(intent);
  const solCostText = voteSolCostText(intent?.solCosts);
  const networkLabel = normalizePrivyVoteNetworkLabel(networkId);
  const feeText = String(estimatedFeeText || "").trim() || fallbackFeeText;
  const walletLabel = formatWalletAddress(walletAddress);

  if (tokenRows.length > 0) {
    const rows = [
      { label: "Option", value: selectedOption?.label || "Selected option" },
      ...tokenRows
    ];
    if (solCostText) rows.push({ label: "SOL costs", value: solCostText });
    if (accountFundingText) rows.push({ label: "Account funding", value: `${accountFundingText} SOL` });
    rows.push(
      { label: "Network", value: networkLabel },
      { label: "Fee", value: feeText },
      { label: "Wallet", value: walletLabel }
    );

    return {
      rows,
      optionLabel: selectedOption?.label || "Selected option",
      voteAmountText: tokenRows[0]?.value || formatMarketTokenAmount(voteAmount, tokenContext),
      networkLabel,
      feeText,
      walletAddress,
      walletLabel
    };
  }

  return {
    optionLabel: selectedOption?.label || "Selected option",
    voteAmountText: formatMarketTokenAmount(voteAmount, tokenContext),
    networkLabel,
    feeText,
    walletAddress,
    walletLabel
  };
}

export function usePrivyVoteConfirmation({ market, wallet }) {
  const [details, setDetails] = useState(null);
  const pendingRef = useRef(null);
  const enabled = isPrivyEmbeddedVoteWallet(wallet);
  const networkId = getPrivyEmbeddedConfig().networkId || "mainnet";

  const settle = useCallback((result) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setDetails(null);
    pending?.resolve(result);
  }, []);

  const confirmEmbeddedTransaction = useCallback(
    (context = {}) => {
      if (!enabled) return null;
      pendingRef.current?.resolve(false);
      return new Promise((resolve) => {
        pendingRef.current = { resolve };
        setDetails(buildPrivyVoteConfirmationDetails({
          market,
          optionId: context.optionId,
          voteAmount: context.voteAmount,
          intent: context.intent,
          networkId,
          expectedUserWallet: context.expectedUserWallet,
          estimatedFeeText: context.estimatedFeeText
        }));
      });
    },
    [enabled, market, networkId]
  );

  useEffect(() => () => {
    pendingRef.current?.resolve(false);
    pendingRef.current = null;
  }, []);

  const modalProps = useMemo(() => ({
    open: Boolean(details),
    details,
    onApprove: () => settle({ skipWalletUi: true }),
    onCancel: () => settle(false)
  }), [details, settle]);

  return {
    confirmEmbeddedTransaction: enabled ? confirmEmbeddedTransaction : undefined,
    modalProps
  };
}

function buildTokenVoteRows({ intent, tokenContext, voteAmount }) {
  const token = marketPaymentTokenForDisplay(tokenContext);
  const grossUnits = intent?.grossVoteUnits ?? intent?.gross_vote_units ?? null;
  if (!grossUnits) return [];

  const rows = [
    { label: "Vote amount", value: formatMarketTokenAmount(voteAmount, token, { units: grossUnits }) }
  ];
  appendTokenRow(rows, "Escrow", intent?.escrowUnits ?? intent?.escrow_units, token);
  appendTokenRow(rows, "Affiliate fee", intent?.affiliateFeeUnits ?? intent?.affiliate_fee_units, token);
  appendTokenRow(rows, "Service fee", intent?.serviceFeeUnits ?? intent?.service_fee_units, token);
  appendTokenRow(rows, "Creator fee", intent?.creatorFeeUnits ?? intent?.creator_fee_units, token);
  appendTokenRow(rows, "Community fee", intent?.collectionFeeUnits ?? intent?.collection_fee_units, token);
  return rows;
}

function appendTokenRow(rows, label, units, token) {
  if (units === null || units === undefined || String(units).trim() === "") return;
  rows.push({ label, value: formatMarketTokenAmount(0, token, { units }) });
}

function voteSolCostText(solCosts) {
  if (!solCosts || typeof solCosts !== "object" || Array.isArray(solCosts)) return "";
  let total = 0n;
  for (const value of Object.values(solCosts)) {
    try {
      total += BigInt(String(value || "0"));
    } catch {
      return "";
    }
  }
  return total > 0n ? `${(Number(total) / 1_000_000_000).toFixed(9).replace(/0+$/, "").replace(/\.$/, "")} SOL` : "";
}
